import {
  ButtonActionLabelInput,
  ButtonMessageFieldsFragment,
  ButtonMessageInputV2,
  CardInputV2,
  LineCardButtonAction,
  LineCardButtonActionType,
} from '@anyx/common-graphql';
import { TrackingUrlArray, toUploadImageFile } from '@anyx/feature-crm/shared';
import { URLUtils } from '@anyx/shared-utils';

import {
  ButtonTypeAction,
  ButtonTypeBlock,
  DEFAULT_BUTTON_TYPE_BLOCK_CARD,
  DEFAULT_BUTTON_TYPE_BLOCK_CARD_ACTION,
  LineMessageBlockType,
  PostbackAction,
} from '../../components';

import { styledTextInputAdapter } from './styled-text-input-adapter';
type ButtonContent = {
  buttonMessageV2: ButtonMessageInputV2;
};

type ButtonAdapter = {
  toGQL: (message: ButtonTypeBlock) => ButtonContent;
  toOriginalUrls: (message: ButtonTypeBlock, trackingUrls: TrackingUrlArray) => string[];
  toMessageBlock: (content: ButtonMessageFieldsFragment) => ButtonTypeBlock;
};

const ButtonPostbackInputMap: Record<PostbackAction, LineCardButtonActionType> = {
  [PostbackAction.enum.POSTBACK]: LineCardButtonActionType.LINKED_POSTBACK_BUTTON_ACTION,
  [PostbackAction.enum.URL_MANUAL]: LineCardButtonActionType.URL_BUTTON_ACTION,
  [PostbackAction.enum.URL_VARIABLE]: LineCardButtonActionType.URL_VARIABLE_BUTTON_ACTION,
  [PostbackAction.enum.CLIPBOARD]: LineCardButtonActionType.URL_BUTTON_ACTION, // FIXME: Clipboard action type is not defined in GQL, using URL_BUTTON_ACTION as a fallback
};

const ButtonPostbackResolveMap: Record<
  NonNullable<LineCardButtonAction['__typename']>,
  PostbackAction
> = {
  LinkedPostbackAction: PostbackAction.enum.POSTBACK,
  UrlAction: PostbackAction.enum.URL_MANUAL,
  UrlVariableAction: PostbackAction.enum.URL_VARIABLE,
  ClipboardAction: PostbackAction.enum.CLIPBOARD,
};

const mapActionLabels = ({
  label,
  url,
  postbackAction,
  messageId,
  urlVariable,
}: ButtonTypeAction): ButtonActionLabelInput => {
  const isLinkedPostback = postbackAction === 'POSTBACK';
  const isUrlVariable = postbackAction === 'URL_VARIABLE';
  const isUrlManual = postbackAction === 'URL_MANUAL';

  return {
    label,
    type: ButtonPostbackInputMap[postbackAction],
    linkedPostback: isLinkedPostback ? { messageId: messageId ?? '' } : undefined,
    url: isUrlManual ? { url: URLUtils.encode(url) } : undefined,
    urlVariable: isUrlVariable ? { variable: urlVariable } : undefined,
  };
};

export const buttonAdapter: ButtonAdapter = {
  toGQL: ({ cards }) => ({
    buttonMessageV2: {
      cards: cards
        .map((card) => {
          return {
            ...(card.image && { imageUrl: card.image.src }),
            ...(card.title.text && {
              title: styledTextInputAdapter.toGQL(card.title),
            }),
            ...(card.description.text && {
              description: styledTextInputAdapter.toGQL(card.description),
            }),
            ...(card.actions.length > 0 && {
              actionLabels: card.actions.map(mapActionLabels),
            }),
            imageBelowDescription: card.isImageBelowDescription,
          };
        })
        .filter((card) => Object.keys(card).length !== 0) as CardInputV2[],
    },
  }),
  toOriginalUrls: ({ cards }, trackingUrls) => {
    return cards.reduce((acc, card): string[] => {
      card.actions.forEach((action) => {
        if (action.postbackAction === PostbackAction.enum.URL_MANUAL) {
          const originalUrl = trackingUrls.find(
            ({ trackingUrl }) => trackingUrl === action.url
          )?.originalUrl;

          if (originalUrl) {
            acc.push(URLUtils.encode(originalUrl));
          }
        }
      });

      return acc;
    }, [] as string[]);
  },
  toMessageBlock: ({ cards }) => ({
    type: LineMessageBlockType.enum.BUTTON,
    cards: cards.map(
      ({
        image: cardImage,
        title,
        description = DEFAULT_BUTTON_TYPE_BLOCK_CARD.description,
        actionLabelsV2,
        imageBelowDescription,
      }) => ({
        image: cardImage?.url ? toUploadImageFile(cardImage.url) : undefined,
        title: styledTextInputAdapter.toMessageBlock(title || DEFAULT_BUTTON_TYPE_BLOCK_CARD.title),
        description: styledTextInputAdapter.toMessageBlock(
          description || DEFAULT_BUTTON_TYPE_BLOCK_CARD.description
        ),
        actions: [...(actionLabelsV2 || [])].map(({ label, action }) => {
          if (!action.__typename) return DEFAULT_BUTTON_TYPE_BLOCK_CARD_ACTION;

          const isUrl = action.__typename === 'UrlAction';
          const isUrlVariable = action.__typename === 'UrlVariableAction';

          return {
            label,
            messageId: action.__typename === 'LinkedPostbackAction' ? action.messageId : undefined,
            postbackAction: ButtonPostbackResolveMap[action.__typename],
            url: isUrl ? URLUtils.decode(action.url) : '',
            urlVariable: isUrlVariable ? action.variable : '',
          };
        }),
        isImageBelowDescription: imageBelowDescription,
      })
    ),
  }),
};
